
import SEO from "@/components/SEO";
import { Separator } from "@/components/ui/separator";
import {
  Users,
  ClipboardList,
  Handshake,
  HeartHandshake,
  UserCheck,
  DollarSign,
  Lock,
  BarChart3,
  Mail,
  CheckCircle2,
} from "lucide-react";

const SectionHeader = ({
  icon: Icon,
  title,
}: {
  icon: React.ElementType;
  title: string;
}) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center">
      <Icon className="w-5 h-5 text-primary" />
    </div>
    <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
      {title}
    </h2>
  </div>
);

const BulletList = ({ items }: { items: string[] }) => (
  <ul className="space-y-3 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const CaregiverThriveCircle = () => {
  return (
    <>
      <SEO
        title="You've Been Invited to Join a ThriveCircle | SeniorThrive"
        description="Learn what a ThriveCircle is, what to expect as a caregiver, and how to get started after receiving your invitation."
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/8 via-primary/4 to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--primary)/0.06),transparent_60%)]" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-20 sm:pt-28 pb-14 sm:pb-20 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-8">
            You've Been Invited to Join a ThriveCircle
          </h1>
          <div className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto space-y-4">
            <p>
              If you received an invitation to SeniorThrive, a family member has
              invited you to support an older adult through their ThriveCircle.
            </p>
            <p>
              SeniorThrive is a secure platform that helps older adults, families,
              and caregivers stay aligned around care, safety, and daily updates at
              home.
            </p>
            <p className="text-foreground font-medium">
              This page explains what that means for you.
            </p>
          </div>
        </div>
      </section>

      {/* What Is a ThriveCircle? */}
      <section className="bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <SectionHeader icon={Users} title="What Is a ThriveCircle?" />
          <div className="text-lg text-muted-foreground space-y-4">
            <p>
              A ThriveCircle is a private, shared space centered around one older
              adult.
            </p>
            <p className="font-medium text-foreground">It connects:</p>
            <BulletList
              items={["The older adult", "Family members", "Caregivers"]}
            />
            <p className="pt-2">
              Everyone stays aligned in one place with clear documentation, visit
              notes, and updates.
            </p>
            <div className="mt-4 rounded-xl p-4 text-base">
              <p className="text-foreground font-medium">
                You are only invited to the specific ThriveCircle connected to
                the person you support. You will not see other families or
                circles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Is ThriveCare? */}
      <section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <SectionHeader icon={ClipboardList} title="What Is Care Team?" />
          <div className="text-lg text-muted-foreground space-y-4">
            <p>
              Care Team is the caregiver workspace inside the ThriveCircle.
            </p>
            <p className="font-medium text-foreground">It allows you to:</p>
            <BulletList
              items={[
                "Log visits clearly and consistently",
                "Track medications and important health details",
                "Document incidents or concerns",
                "Share structured updates with the family",
                "Keep everything organized in one place",
              ]}
            />
            <p className="pt-2">
              Instead of scattered texts, paper notes, or repeated phone calls,
              everything lives in one secure system.
            </p>
            <p className="text-foreground font-medium">
              It brings structure to caregiving without adding complexity.
            </p>
          </div>
        </div>
      </section>

      <Separator className="max-w-2xl mx-auto" />

      {/* How ThriveCare Supports You */}
      <section className="bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <SectionHeader icon={Handshake} title="How Care Team Supports You" />
          <div className="text-lg text-muted-foreground space-y-4">
            <p className="text-foreground font-medium">
              Care Team is designed to support caregivers, not monitor them.
            </p>
            <p>It helps you:</p>
            <BulletList
              items={[
                "Create a clear record of the care you provide",
                "Reduce misunderstandings with family members",
                "Keep communication organized",
                "Protect your time with structured documentation",
                "Show your professionalism through consistent updates",
              ]}
            />
            <p className="pt-2">
              Your work becomes visible and organized without extra
              back-and-forth.
            </p>
          </div>
        </div>
      </section>

      {/* Caring for the Caregiver */}
      <section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <SectionHeader
            icon={HeartHandshake}
            title="Caring for the Caregiver"
          />
          <div className="text-lg text-muted-foreground space-y-4">
            <p className="text-foreground font-medium">
              Caregiving is demanding.
            </p>
            <p>
              SeniorThrive includes resources designed to support you as well, not
              just the person you're caring for.
            </p>
            <p>Inside the ThriveCircle, you'll find:</p>
            <BulletList
              items={[
                "Gentle check-ins that help you stay aware of your own well-being",
                "Structured updates that reduce communication stress",
                "A clear system that prevents things from falling through the cracks",
                "A shared record so you don't have to carry everything alone",
              ]}
            />
            <p className="pt-2">
              When care is organized, your day becomes more manageable.
            </p>
            <p className="text-foreground font-medium">
              You're part of the ThriveCircle, too.
            </p>
          </div>
        </div>
      </section>

      <Separator className="max-w-2xl mx-auto" />

      {/* What Will Be Expected of You? */}
      <section className="bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <SectionHeader
            icon={UserCheck}
            title="What Will Be Expected of You?"
          />
          <div className="text-lg text-muted-foreground space-y-4">
            <p>
              As a caregiver in a ThriveCircle, your role is simple:
            </p>
            <p className="text-foreground text-xl font-bold">
              Log your visits and share relevant updates.
            </p>
            <div className="rounded-xl border border-border bg-card p-5 space-y-2 text-base">
              <p>You are not managing billing.</p>
              <p>You are not responsible for platform payments.</p>
              <p>You are not expected to learn a complex system.</p>
            </div>
            <p className="pt-1">
              Your primary action is documenting care so everyone stays aligned.
            </p>
          </div>
        </div>
      </section>

      {/* Is There a Cost to Join? */}
      <section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <SectionHeader icon={DollarSign} title="Is There a Cost to Join?" />
          <div className="text-lg text-muted-foreground space-y-4">
            <p className="text-foreground text-3xl font-bold">No.</p>
            <p>
              Access to the ThriveCircle has been provided by the family who
              invited you.
            </p>
            <p>There is no subscription fee for caregivers.</p>
            <p className="text-foreground font-medium">
              You will not be asked for payment.
            </p>
          </div>
        </div>
      </section>

      <Separator className="max-w-2xl mx-auto" />

      {/* Is My Access Secure? */}
      <section className="bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <SectionHeader icon={Lock} title="Is My Access Secure?" />
          <div className="text-lg text-muted-foreground space-y-4">
            <p className="text-foreground font-bold text-xl">Yes.</p>
            <BulletList
              items={[
                "Your access is limited to the specific ThriveCircle you were invited to.",
                "You cannot see other families or circles.",
                "SeniorThrive is designed to keep care communication organized, structured, and secure.",
              ]}
            />
          </div>
        </div>
      </section>

      {/* Why Families Use SeniorThrive */}
      <section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <SectionHeader
            icon={BarChart3}
            title="Why Families Use SeniorThrive"
          />
          <div className="text-lg text-muted-foreground space-y-4">
            <p>Families use SeniorThrive to:</p>
            <BulletList
              items={[
                "Stay informed without constant texting",
                "Keep documentation consistent",
                "Create a reliable care record",
                "Reduce misunderstandings",
                "Support both the older adult and the caregiver",
              ]}
            />
            <p className="pt-2 text-foreground font-medium">
              It brings clarity to caregiving for everyone involved.
            </p>
          </div>
        </div>
      </section>

      {/* What Happens Next? */}
      <section className="relative overflow-hidden bg-gradient-to-b from-muted/40 via-primary/5 to-primary/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,hsl(var(--primary)/0.06),transparent_60%)]" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <SectionHeader icon={Mail} title="What Happens Next?" />
          <div className="text-lg text-muted-foreground space-y-4 mb-10 text-left sm:text-center">
            <p>If you're ready to join:</p>
            <p className="text-foreground font-bold text-xl">
              Return to your invitation email and click "Create Your Free
              Account."
            </p>
            <p>
              If you already created an account, sign in to access the
              ThriveCircle.
            </p>
          </div>

        </div>
      </section>
    </>
  );
};

export default CaregiverThriveCircle;
